/**
 * Evaluates a single rule against the provided data.
 * @param {Object} rule - The rule to evaluate, containing a path and optional conditions.
 * @param {string[]} rule.path - An array of strings representing the path to the value in the data object.
 * @param {any} [rule.is] - An optional condition that specifies the value must be equal to this for the rule to pass.
 * @param {any} [rule.isNot] - An optional condition that specifies the value must not be equal to this for the rule to pass.
 * @param {Record<string, any>} data - The data object to evaluate the rule against.
 * @returns {boolean} - Returns true if the rule is satisfied, otherwise false.
 */
const evaluateRule = (
  rule: CMxCommonApp.ValueAssertion,
  data: Record<string, any>
): boolean => {
  const value = rule.path.reduce(
    (obj, key) => (obj ? obj[key] : undefined),
    data
  );

  // Check `is` and `isNot` conditions
  return (
    (rule.is === undefined || value === rule.is) &&
    (rule.isNot === undefined || value !== rule.isNot)
  );
};

/**
 * Evaluates whether the input should be enabled based on the given rule.
 * @param rule - Array of PathAssertion objects that must all evaluate to true for the input to be enabled.
 * @param data - The data object to evaluate the rule against.
 * @param logicType - Select logical operation. If "AND", all rules must be true for the input to be enabled. If "OR", at least one rule must be true for the input to be enabled.
 * @returns {boolean} - True if all assertions are met, otherwise false.
 */
export default function evaluateEnablerRules(
  rules: CMxCommonApp.InputEnableRules,
  data: Record<string, any>,
  logicType: 'AND' | 'OR' = 'OR' // Default to "OR"
): boolean {
  // Iterate through the rules with short-circuiting
  for (const rule of rules) {
    const rulePasses = evaluateRule(rule, data);

    if (logicType === 'OR' && rulePasses) {
      return true; // Short-circuit for "OR"
    }

    if (logicType === 'AND' && !rulePasses) {
      return false; // Short-circuit for "AND"
    }
  }

  // Return the appropriate default based on the logic type
  return logicType === 'AND'; // All rules passed for "AND", or none passed for "OR"
}

//Exported for testing
export const FieldEnableRules = {
  evaluateRule
};
